import { FC } from "react";
import { Link } from "react-router-dom";
import withPage from "../../components/Page";
import Section from "../../components/Section";
import { globalContent } from "../../config/content";
import styles from "./PrivacyPolicy.module.scss";

const PrivacyPolicy: FC = () => {
  return (
    <div className={styles.privacyPage}>
      <Section bgColor="white" sectionClass={styles.legalSection}>
        <div className={styles.legalContainer}>
          <div className={styles.pageHeader}>
            <h1 className={styles.h1}>Privacy Policy</h1>
            <p className={styles.leadText}>
              This Privacy Policy explains how we collect, use and protect personal information when you use the Pulse website.
            </p>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>About this policy</h2>
            <p className={styles.paragraph}>
              This Privacy Policy explains how we collect, use and protect personal information when you use the Pulse website.
            </p>
            <p className={styles.paragraph}>
              We respect your privacy and are committed to handling your personal information responsibly and transparently.
            </p>
            <p className={styles.paragraph}>
              This policy applies to personal information collected through this website and explains your rights in relation to that information.
            </p>
          </div>



          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>Information we collect</h2>
            <p className={styles.paragraph}>
              We may collect personal information when you interact with the website or contact us.
            </p>

            <h3 className={styles.subHeading}>Information you provide</h3>
            <p className={styles.paragraph}>
              This may include information such as your name, email address, company name and any information you choose to include when contacting us or requesting information about Pulse.
            </p>

            <h3 className={styles.subHeading}>Information collected automatically</h3>
            <p className={styles.paragraph}>
              When you visit the website, limited technical information may be processed as part of providing and securing the website.
            </p>
            <p className={styles.paragraph}>
              This may include information such as your IP address, browser type, device information and basic information about how the website is accessed.
            </p>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>How we use your information</h2>
            <p className={styles.paragraph}>
              We may use personal information to:
            </p>
            <ul className={styles.list}>
              <li>Respond to enquiries and requests for information.</li>
              <li>Communicate with you about Pulse.</li>
              <li>Arrange or respond to requests for demonstrations.</li>
              <li>Provide information or support that you have requested.</li>
              <li>Maintain and secure the website.</li>
              <li>Comply with legal and regulatory obligations.</li>
              <li>Protect our rights and prevent misuse of the website.</li>
            </ul>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>Our legal basis for using your information</h2>
            <p className={styles.paragraph}>
              Where required by applicable data protection law, we rely on an appropriate legal basis when processing personal information.
            </p>
            <p className={styles.paragraph}>
              Depending on the circumstances, this may include:
            </p>
            <ul className={styles.list}>
              <li><strong>Consent</strong>, where you have given us permission to use your information for a specific purpose.</li>
              <li><strong>Legitimate interests</strong>, where processing is necessary for legitimate business purposes and those interests are not overridden by your rights.</li>
              <li><strong>Contract</strong>, where processing is necessary to take steps at your request or perform a contract.</li>
              <li><strong>Legal obligation</strong>, where we are required to process information to comply with the law.</li>
            </ul>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>Who we share your information with</h2>
            <p className={styles.paragraph}>
              We do not sell your personal information.
            </p>
            <p className={styles.paragraph}>
              We may share personal information with service providers where this is necessary to operate the website, communicate with you or provide services you have requested.
            </p>
            <p className={styles.paragraph}>
              Where third-party providers process personal information on our behalf, we take appropriate steps to ensure that the information is handled securely and in accordance with applicable data protection requirements.
            </p>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>International transfers</h2>
            <p className={styles.paragraph}>
              Some service providers may process information outside the United Kingdom.
            </p>
            <p className={styles.paragraph}>
              Where personal information is transferred internationally, we will take appropriate steps to ensure that the transfer is lawful and that appropriate safeguards are in place where required.
            </p>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>How long we keep your information</h2>
            <p className={styles.paragraph}>
              We keep personal information only for as long as necessary for the purposes for which it was collected, including where necessary to meet legal, accounting or reporting requirements.
            </p>
            <p className={styles.paragraph}>
              The appropriate retention period depends on the type of information and why it was collected.
            </p>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>How we protect your information</h2>
            <p className={styles.paragraph}>
              We take reasonable technical and organisational measures to protect personal information against unauthorised access, loss, misuse, alteration or disclosure.
            </p>
            <p className={styles.paragraph}>
              However, no method of transmitting or storing information can be guaranteed to be completely secure.
            </p>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>Your rights</h2>
            <p className={styles.paragraph}>
              Under applicable UK data protection law, you may have rights including:
            </p>
            <ul className={styles.list}>
              <li>The right to access the personal information we hold about you.</li>
              <li>The right to request correction of inaccurate or incomplete information.</li>
              <li>The right to request deletion of your personal information in certain circumstances.</li>
              <li>The right to restrict processing in certain circumstances.</li>
              <li>The right to object to certain processing.</li>
              <li>The right to data portability in certain circumstances.</li>
              <li>The right to withdraw consent where processing is based on consent.</li>
            </ul>
            <p className={styles.paragraph}>
              You also have the right to make a complaint to the{" "}
              <a
                href="https://ico.org.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                Information Commissioner's Office (ICO)
              </a>{" "}
              if you believe your personal information has been handled unlawfully.
            </p>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>Cookies</h2>
            <p className={styles.paragraph}>
              This website may use cookies or similar technologies where necessary to provide or secure website functionality.
            </p>
            <p className={styles.paragraph}>
              For more information, please see our <Link to="/cookie-policy" className={styles.link}>Cookie Policy</Link>.
            </p>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>Links to other websites</h2>
            <p className={styles.paragraph}>
              The website may contain links to third-party websites.
            </p>
            <p className={styles.paragraph}>
              We are not responsible for the privacy practices or content of websites that we do not operate. We recommend reviewing the privacy policy of any third-party website you visit.
            </p>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>Changes to this policy</h2>
            <p className={styles.paragraph}>
              We may update this Privacy Policy from time to time to reflect changes to the website, our services or applicable legal requirements.
            </p>
            <p className={styles.paragraph}>
              When we make changes, we will update the date shown at the bottom of this page.
            </p>
          </div>

          <div className={styles.sectionBlock}>
            <h2 className={styles.sectionHeading}>Contact us</h2>
            <p className={styles.paragraph}>
              If you have a question about this Privacy Policy or how we handle personal information, please visit our <Link to="/contact" className={styles.link}>Contact page</Link>.
            </p>
          </div>

          <div className={styles.lastUpdated}>
            Last updated: October 2026
          </div>
        </div>
      </Section>
    </div>
  );
};

export default withPage(PrivacyPolicy)({
  meta: globalContent.seo.privacy,
});
